import { flow, initial, last, mapKeys } from "lodash";
import type { RouteRecordRaw, RouteRecordRedirectOption } from "vue-router";
import { home } from "./guards/home";

declare module "vue-router" {
  interface RouteMeta {
    authRequired?: boolean;
    roles?: Array<string>;
    privilege?: { resource: string; attribute: string };
  }
}

/**
 * For every folder in 'pages' match the file named as the folder eg pages/Wizard/Wizard.vue
 * The rest of the files are used in the page but need not be made available
 *
 * \\1 is equivalent to \1 in regex, matches the capturing group number 1
 */
const modules = import.meta.glob("@/pages/**/*.vue");
// Takes a path and returns a capitalized module name
const getModuleName = flow([(value, key) => key.split(/[/.]+/g), initial, last]);

// Makes a map of all the pages in the application
const pages = mapKeys(modules, getModuleName);

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: { name: "welcome" },
  },
  {
    path: "/home",
    name: "home",
    redirect: home as RouteRecordRedirectOption,
  },
  {
    path: "/login",
    name: "login",
    component: pages.Login,
  },
  {
    path: "/logged-user-profile",
    name: "logged-user-profile",
    component: pages.LoggedUserProfile,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/placeholder",
    name: "placeholder",
    component: pages.Placeholder,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/welcome",
    name: "welcome",
    component: pages.Welcome,
  },
  {
    path: "/suspended-user",
    name: "suspended-user",
    component: pages.SuspendedUser,
  },

  {
    path: "/wrong-role",
    name: "wrong-role",
    component: pages.WrongRole,
    // meta: {
    //   authRequired: true,
    // },
  },
  {
    path: "/translations",
    name: "translations",
    component: pages.Translation,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
      privilege: {
        resource: "traslations_page",
        attribute: "view",
      },
    },
  },
  {
    path: "/additional-agreement",
    name: "additional-agreement",
    component: pages.AdditionalAgreement,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/designer-dashboard",
    name: "designer-dashboard",
    component: pages.DesignerDashboard,
    meta: {
      authRequired: true,
      // roles: ["Designer"],
    },
  },
  {
    path: "/specialist-dashboard",
    name: "specialist-dashboard",
    component: pages.SpecialistDashboard,
    meta: {
      authRequired: true,
      roles: ["Specialist"],
    },
  },
  {
    path: "/backoffice-dashboard",
    name: "backoffice-dashboard",
    component: pages.BackofficeDashboard,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
    },
  },
  {
    path: "/bank-service",
    component: pages.BankService,
    meta: {
      authRequired: true,
      // roles: ["Designer"],
    },
    children: [
      {
        name: "bank-service",
        path: "",
        component: () => import("@/pages/BankService/BankServiceIndex.vue"),
      },
    ],
  },
  {
    path: "/designer-network",
    component: pages.DesignerNetwork,
    meta: {
      authRequired: true,
      // roles: ["Designer"],
    },
    children: [
      {
        path: "",
        name: "designer-network",
        component: () => import("@/pages/DesignerNetwork/DesignerNetworkDetails.vue"),
      },
    ],
  },
  {
    path: "/appointment",
    component: pages.Appointment,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "appointment",
        component: () => import("@/pages/Appointment/AppointmentIndex.vue"),
      },
    ],
  },
  {
    path: "/tutor",
    component: pages.Tutor,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "tutor",
        component: () => import("@/pages/Tutor/TutorIndex.vue"),
      },
      {
        name: "tutor-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Tutor/TutorEdit.vue"),
      },
      {
        name: "tutor-assign",
        path: "assign/:id?",
        component: () => import("@/pages/Tutor/TutorAssign.vue"),
      },
      // {
      //   name: "tutor-profile",
      //   path: "assign/:id/profile/:profileId",
      //   component: () => import("@/pages/Tutor/TutorProfile.vue"),
      // },
    ],
  },
  {
    path: "/agora-professional",
    component: pages.AgoraProfessional,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "agora-professional",
        component: () => import("@/pages/AgoraProfessional/AgoraProfessionalIndex.vue"),
      },
      {
        path: ":userId",
        name: "agora-professional-details",
        component: () => import("@/pages/BOProfessional/BOProfessionalDetails.vue"),
      },
    ],
  },
  {
    path: "/specialist",
    component: pages.Specialist,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "specialist",
        component: () => import("@/pages/Specialist/SpecialistIndex.vue"),
      },
    ],
  },
  {
    path: "/bo-professional",
    component: pages.BOProfessional,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser", "Specialist"],
    },
    children: [
      {
        path: "",
        name: "bo-professional",
        component: () => import("@/pages/BOProfessional/BOProfessionalIndex.vue"),
      },
      {
        path: ":userId",
        name: "bo-professional-details",
        component: () => import("@/pages/BOProfessional/BOProfessionalDetails.vue"),
      },
    ],
  },
  {
    path: "/networkcoach-assignation/:id",
    component: pages.NetworkCoachAssignation,
    meta: {
      authRequired: true,
    },
    children: [
      {
        name: "networkcoach-assignation",
        path: "",
        component: () => import("@/pages/NetworkCoachAssignation/NetworkCoachAssignation.vue"),
      },
    ],
  },
  {
    path: "/opportunity",
    component: pages.Opportunity,
    meta: {
      authRequired: true,
    },
    children: [
      {
        name: "opportunity",
        path: "",
        component: () => import("@/pages/Opportunity/OpportunityIndex.vue"),
      },
      {
        name: "opportunity-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Opportunity/OpportunityEdit.vue"),
      },
    ],
  },
  {
    path: "/opportunity-register",
    component: pages.OpportunityRegister,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "opportunity-register",
        component: () => import("@/pages/OpportunityRegister/OpportunityRegister.vue"),
      },
    ],
  },
  // {
  //   path: "/training-module",
  //   component: pages.TrainingModule,
  //   meta: {
  //     authRequired: true,
  //   },
  //   children: [
  //     {
  //       path: "",
  //       name: "training-module",
  //       component: () => import("@/pages/TrainingModule/TrainingModuleIndex.vue"),
  //     },
  //     {
  //       path: "program/:id",
  //       name: "training-module-detail",
  //       component: () => import("@/pages/TrainingModule/TrainingModuleDetail.vue"),
  //     },
  //     {
  //       path: "lesson/:id",
  //       name: "training-module-lesson-detail",
  //       component: () => import("@/pages/TrainingModule/TrainingModuleLesson.vue"),
  //     },
  //   ],
  // },
  // {
  //   path: "/backoffice-training",
  //   component: pages.BackOfficeTraining,
  //   meta: {
  //     authRequired: true,
  //     roles: ["BackofficeUser"],
  //   },
  //   children: [
  //     {
  //       path: "",
  //       name: "backoffice-training",
  //       component: () => import("@/pages/BackOfficeTraining/BackOfficeTrainingModuleIndex.vue"),
  //     },
  //     {
  //       path: "module-form/:id?",
  //       name: "backoffice-training-module-form",
  //       component: () => import("@/pages/BackOfficeTraining/BackOfficeTrainingModuleForm.vue"),
  //     },
  //     {
  //       path: "module/:moduleId/lesson-form/:id?",
  //       name: "backoffice-training-lesson-form",
  //       component: () => import("@/pages/BackOfficeTraining/BackOfficeTrainingLessonForm.vue"),
  //     },
  //     {
  //       path: "module-detail/:id",
  //       name: "backoffice-training-module-detail",
  //       component: () => import("@/pages/BackOfficeTraining/BackOfficeTrainingModuleDetail.vue"),
  //     },
  //     {
  //       path: "module-preview/:id",
  //       name: "backoffice-training-module-preview",
  //       component: () => import("@/pages/BackOfficeTraining/BackOfficeTrainingModulePreview.vue"),
  //     },
  //     {
  //       path: "lesson-preview/:id",
  //       name: "backoffice-training-lesson-preview",
  //       component: () => import("@/pages/BackOfficeTraining/BackOfficeTrainingLessonPreview.vue"),
  //     },
  //   ],
  // },
  {
    path: "/projects",
    component: pages.Projects,
    meta: {
      authRequired: true,
    },
    children: [
      {
        name: "projects",
        path: "",
        component: () => import("@/pages/Projects/ProjectsIndex.vue"),
      },
      {
        name: "projects-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Projects/ProjectsEdit.vue"),
      },
    ],
  },
  {
    path: "/project-landing/:scope?/:id",
    component: pages.ProjectLanding,
    meta: {
      authRequired: true,
    },
    children: [
      {
        name: "project-landing",
        path: "",
        component: () => import("@/pages/ProjectLanding/ProjectLanding.vue"),
      },
    ],
  },
  {
    path: "/project-bt/:id/bt/:childId?",
    component: pages.ProjectBt,
    meta: {
      authRequired: true,
    },
    children: [
      {
        name: "project-bt",
        path: "",
        component: () => import("@/pages/ProjectBt/ProjectBt.vue"),
      },
    ],
  },
  {
    path: "/notifications",
    component: pages.Notification,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "notifications",
        component: () => import("@/pages/Notification/NotificationIndex.vue"),
      },
    ],
  },
  {
    path: "/formazione",
    component: pages.Formazione,
    meta: {
      authRequired: true,
    },
    children: [
      {
        name: "formazione",
        path: "",
        component: () => import("@/pages/Formazione/FormazioneIndex.vue"),
      },
    ],
  },
  {
    path: "/formazionespecialist",
    component: pages.SpecialistFormazione,
    meta: {
      authRequired: true,
      // roles: ['BackofficeUser']
    },
    children: [
      {
        name: "formazionespecialist",
        path: "",
        component: () => import("@/pages/SpecialistFormazione/SpecialistFormazioneIndex.vue"),
      },
      {
        name: "formazionespecialist-course",
        path: "formazionespecialist/course/:id?",
        component: () => import("@/pages/SpecialistFormazione/SpecialistFormazioneCouse.vue"),
      },
    ],
  },
  {
    path: "/hw-networks",
    component: pages.HWNetworks,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "hw-networks",
        component: () => import("@/pages/HWNetworks/HWNetworksIndex.vue"),
      },
      {
        path: "detail/:id",
        name: "hw-networks-detail",
        component: () => import("@/pages/HWNetworks/HWNetworksDetail.vue"),
      },
      // {
      //   path: 'designer/:networkId/:id',
      //   name: 'hw-networks-designer',
      //   // component: () => import('@/pages/HWNetworks/HWNetworksDesigner.vue')
      // },
    ],
  },
  {
    path: "/network-assignation",
    component: pages.NetworkAssignation,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "network-assignation",
        component: () => import("@/pages/NetworkAssignation/NetworkAssignationIndex.vue"),
      },
    ],
  },
  {
    path: "/network",
    component: pages.Network,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "network",
        component: () => import("@/pages/Network/NetworkDetails.vue"),
      },
    ],
  },
  {
    path: "/dev",
    name: "dev",
    component: pages.Dev,
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: { name: "home" },
  },
];

export default routes;
